import { useCallback, useMemo } from 'react';
import type {
  ExchangeProjectContextDto,
  ExchangeProjectContextItemDto,
} from '@inference.engine/api-client';
import type { ArtifactKind } from '@inference.engine/widgets';
import { nextAttachmentOrder } from '@/features/chats/lib/attachmentOrder';
import { rebuildProjectContextSelection } from '@/features/chats/model/projectContextSelection.utils';
import {
  useProjectContextSelection,
  useSetInputOptions,
  type ProjectContextSelection,
} from '@/features/chats/stores';

/**
 * Lets a past message's project-context chips be re-attached to the next
 * message or dropped again, mirroring how attached files re-reference by
 * assetId. Re-referencing a brief restores its inline snapshot sections so the
 * composer re-sends the exact content the message carried; an artifact is
 * re-attached by id. Selection stays single-project, so re-referencing from a
 * different project's message replaces the in-flight selection.
 *
 * Exposes `briefById` (built once from the snapshot) so the rendering component
 * resolves a chip's brief without recomputing the same map.
 */
export const useChatMessageProjectContext = (projectContext: ExchangeProjectContextDto) => {
  const setOptions = useSetInputOptions();
  const selection = useProjectContextSelection();

  const referencedIds = useMemo(
    () => new Set(selection?.items.map((item) => item.id) ?? []),
    [selection],
  );

  const briefById = useMemo(
    () => new Map((projectContext.briefs ?? []).map((brief) => [brief.id, brief])),
    [projectContext.briefs],
  );

  const isReferenced = useCallback((id: string) => referencedIds.has(id), [referencedIds]);

  const reference = useCallback(
    (item: ExchangeProjectContextItemDto) => {
      setOptions((prev) => {
        const current = prev?.projectContext as ProjectContextSelection | undefined;
        // Selection is single-project: keep building on it only when the chip
        // belongs to the same project, otherwise start fresh.
        const base: ProjectContextSelection =
          current && current.projectId === projectContext.projectId
            ? current
            : { projectId: projectContext.projectId, briefs: [], artifactIds: [], items: [] };

        if (base.items.some((existing) => existing.id === item.id)) return prev;

        const snapshotBrief = briefById.get(item.id);
        const briefs =
          item.kind === 'brief' && snapshotBrief ? [...base.briefs, snapshotBrief] : base.briefs;
        const items = [
          ...base.items,
          {
            id: item.id,
            name: item.title,
            kind: item.kind as ArtifactKind,
            addedAt: nextAttachmentOrder(),
          },
        ];

        return {
          ...(prev ?? {}),
          projectContext: rebuildProjectContextSelection({ ...base, briefs }, items),
        };
      });
    },
    [setOptions, projectContext.projectId, briefById],
  );

  const remove = useCallback(
    (id: string) => {
      setOptions((prev) => {
        const current = prev?.projectContext as ProjectContextSelection | undefined;
        if (!current) return prev;

        return {
          ...(prev ?? {}),
          projectContext: rebuildProjectContextSelection(
            current,
            current.items.filter((item) => item.id !== id),
          ),
        };
      });
    },
    [setOptions],
  );

  return { isReferenced, reference, remove, briefById };
};
